import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../components/Common/Breadcrumb";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner,
} from "reactstrap";
import {
  fetchAllCancellationsRequest,
  fetchCancellationStatisticsRequest,
  applyCancellationFineRequest,
  updateCancellationStatusRequest,
} from "../../store/cancellations/actions";
import { toast } from "react-toastify";
import "./cancellation-tracking.css";
import TableContainer from "../../components/Common/TableContainer";
import { db } from "../../helpers/firebase/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { applyPendingProviderFineToNextSettlement } from "../../helpers/api/cancellationHelper";

const CancellationTracking = () => {
  const dispatch = useDispatch();
  const { cancellations, statistics, loading, error, fineLoading } =
    useSelector((state) => state.cancellations);

  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [fineAmount, setFineAmount] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProvider, setFilterProvider] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingProviderFines, setPendingProviderFines] = useState([]);
  const [pendingFinesLoading, setPendingFinesLoading] = useState(false);
  const [applyingPendingFineId, setApplyingPendingFineId] = useState(null);

  const isDelayIncident = (record) =>
    String(record?.cancellation_type || record?.incident_type || "").toLowerCase() === "provider_delay";

  const loadPendingProviderFines = async () => {
    try {
      setPendingFinesLoading(true);
      const snapshot = await getDocs(collection(db, "pending_fine_deductions"));
      const rows = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter(
          (row) =>
            row.charged_to === "provider" &&
            ["pending", "partial"].includes(String(row.status || "").toLowerCase())
        )
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      setPendingProviderFines(rows);
    } catch (loadError) {
      console.error("Error loading pending provider fines:", loadError);
      toast.error("تعذر تحميل الغرامات المعلقة لمقدمي الخدمة");
    } finally {
      setPendingFinesLoading(false);
    }
  };

  const handleApplyPendingFine = async (pendingFineId) => {
    try {
      setApplyingPendingFineId(pendingFineId);
      const result = await applyPendingProviderFineToNextSettlement(pendingFineId);

      toast.success(
        `تم خصم ${Number(result?.deductedAmount || 0).toFixed(2)} ر.س من التسوية القادمة. المتبقي ${Number(
          result?.remainingPendingAmount || 0
        ).toFixed(2)} ر.س`
      );

      await loadPendingProviderFines();
      dispatch(fetchAllCancellationsRequest());
      dispatch(fetchCancellationStatisticsRequest());
    } catch (applyError) {
      console.error("Error applying pending provider fine:", applyError);
      toast.error(applyError?.message || "تعذر تطبيق الغرامة على التسوية القادمة");
    } finally {
      setApplyingPendingFineId(null);
    }
  };

  useEffect(() => {
    dispatch(fetchAllCancellationsRequest());
    dispatch(fetchCancellationStatisticsRequest());
    loadPendingProviderFines();
  }, [dispatch]);

  useEffect(() => {
    loadPendingProviderFines();
  }, [fineLoading]);

  // Filter cancellations
  const filteredCancellations = useMemo(() => {
    return cancellations.filter((c) => {
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "deducted"
          ? c.deduction_status === "deducted"
          : filterStatus === "pending"
            ? ["pending", "warning_issued", "confirmed"].includes(c.status)
            : c.status === filterStatus);

      const matchesProvider =
        filterProvider === "all" ||
        c.service_provider_id === filterProvider;

      const matchesSearch =
        !searchTerm ||
        c.service_provider_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        c.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.service_name?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesProvider && matchesSearch;
    });
  }, [cancellations, filterStatus, filterProvider, searchTerm]);

  // Get unique providers for filter
  const providers = useMemo(() => {
    const uniqueProviders = {};
    cancellations.forEach((c) => {
      if (c.service_provider_id && !uniqueProviders[c.service_provider_id]) {
        uniqueProviders[c.service_provider_id] = {
          id: c.service_provider_id,
          name:
            c.provider_details?.name ||
            c.service_provider_name ||
            "Unknown Provider",
        };
      }
    });
    return Object.values(uniqueProviders);
  }, [cancellations]);

  // Handle fine application
  const handleApplyFine = () => {
    if (!fineAmount || isNaN(fineAmount) || parseFloat(fineAmount) <= 0) {
      toast.error("Please enter a valid fine amount");
      return;
    }

    dispatch(
      applyCancellationFineRequest(
        selectedCancellation.id,
        parseFloat(fineAmount)
      )
    );
    setShowFineModal(false);
    setFineAmount("");
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "success";
      case "disputed":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Get deduction status badge
  const getDeductionStatusBadge = (deductionStatus) => {
    switch (deductionStatus) {
      case "deducted":
        return { color: "success", text: "مخصوم" };
      case "pending":
        return { color: "warning", text: "قيد الانتظار" };
      case "pending_settlement":
        return { color: "warning", text: "بانتظار التسوية" };
      case "warning_only":
        return { color: "info", text: "إنذار فقط" };
      case "partial":
        return { color: "warning", text: "خصم جزئي" };
      case "failed":
        return { color: "danger", text: "فشل" };
      default:
        return { color: "secondary", text: "غير محدد" };
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: (row, index) => index + 1,
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "مقدم الخدمة",
        accessor: "service_provider_name",
        Cell: ({ row }) => (
          <div>
            <div className="fw-bold">
              {row.original.provider_details?.name ||
                row.original.service_provider_name ||
                "Unknown"}
            </div>
            <small className="text-muted">
              {row.original.provider_details?.email ||
                row.original.service_provider_email}
            </small>
          </div>
        ),
      },
      {
        Header: "الخدمة",
        accessor: "service_name",
        Cell: ({ value }) => <span className="text-muted">{value}</span>,
      },
      {
        Header: "رقم الطلب",
        accessor: "order_id",
      },
      {
        Header: "تاريخ الإلغاء",
        accessor: "cancellation_date",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "النوع",
        accessor: "cancellation_type",
        Cell: ({ value }) => (
          <Badge color={value === "early_cancellation" ? "info" : value === "provider_delay" ? "warning" : "danger"}>
            {value === "early_cancellation"
              ? "مبكر"
              : value === "provider_delay"
                ? "تأخير مقدم الخدمة"
                : "متأخر"}
          </Badge>
        ),
      },
      {
        Header: "إلغاء من",
        accessor: "cancelled_by",
        Cell: ({ value }) => (
          <Badge color={value === "app_user" ? "info" : "warning"}>
            {value === "app_user" ? "العميل" : "مقدم الخدمة"}
          </Badge>
        ),
      },
      {
        Header: "الغرامة",
        accessor: "total_fine",
        Cell: ({ value }) => (
          <span className="fw-bold text-danger">{value?.toFixed(2)} ر.س</span>
        ),
      },
      {
        Header: "حالة الخصم",
        accessor: "deduction_status",
        Cell: ({ value }) => {
          const badge = getDeductionStatusBadge(value);
          return <Badge color={badge.color}>{badge.text}</Badge>;
        },
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              outline
              onClick={() => {
                setSelectedCancellation(row.original);
                setShowDetailsModal(true);
              }}
            >
              <i className="bx bx-show"></i>
            </Button>
            {!isDelayIncident(row.original) && row.original.deduction_status !== "deducted" && (
              <Button
                color="danger"
                size="sm"
                outline
                onClick={() => {
                  setSelectedCancellation(row.original);
                  setFineAmount(row.original.total_fine?.toString() || "");
                  setShowFineModal(true);
                }}
              >
                <i className="bx bx-money"></i>
              </Button>
            )}
          </div>
        ),
      },
    ],
    []
  );

  // Provider statistics
  const providerStats = useMemo(() => {
    if (!statistics?.byProvider) return [];
    return Object.entries(statistics.byProvider)
      .map(([id, data]) => ({
        id,
        ...data,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [statistics]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumb
          maintitle="تتبع الإلغاءات والمخالفات"
          title="الإدارة"
          breadcrumbItem="تتبع إلغاءات ومخالفات مقدمي الخدمة"
        />

        <Container fluid>
          {/* Statistics Cards */}
          {statistics && (
            <Row className="mb-4">
              <Col md="3">
                <Card className="card-stats bg-gradient-primary text-white">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="text-white-50 mb-1">
                          إجمالي الإلغاءات
                        </h6>
                        <h3 className="mb-0">{statistics.total}</h3>
                      </div>
                      <div className="avatar-sm">
                        <span className="avatar-title bg-white bg-soft rounded-circle">
                          <i className="bx bx-x-circle font-size-24 text-primary"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-stats bg-gradient-danger text-white">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="text-white-50 mb-1">
                          إجمالي الغرامات
                        </h6>
                        <h3 className="mb-0">
                          {statistics.totalFines?.toFixed(2)} ر.س
                        </h3>
                      </div>
                      <div className="avatar-sm">
                        <span className="avatar-title bg-white bg-soft rounded-circle">
                          <i className="bx bx-money font-size-24 text-danger"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-stats bg-gradient-warning text-white">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="text-white-50 mb-1">
                          غرامات قيد الانتظار
                        </h6>
                        <h3 className="mb-0">
                          {statistics.totalPendingFines?.toFixed(2)} ر.س
                        </h3>
                      </div>
                      <div className="avatar-sm">
                        <span className="avatar-title bg-white bg-soft rounded-circle">
                          <i className="bx bx-time font-size-24 text-warning"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-stats bg-gradient-success text-white">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="text-white-50 mb-1">غرامات مخصومة</h6>
                        <h3 className="mb-0">
                          {statistics.totalDeductedFines?.toFixed(2)} ر.س
                        </h3>
                      </div>
                      <div className="avatar-sm">
                        <span className="avatar-title bg-white bg-soft rounded-circle">
                          <i className="bx bx-check-circle font-size-24 text-success"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Provider Statistics */}
          {providerStats.length > 0 && (
            <Row className="mb-4">
              <Col lg="12">
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      أعلى 10 مقدمي خدمة بالمخالفات
                    </h4>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>مقدم الخدمة</th>
                            <th>عدد الإلغاءات</th>
                            <th>إجمالي الغرامات</th>
                            <th>غرامات معلقة</th>
                            <th>غرامات مخصومة</th>
                            <th>الإجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {providerStats.map((provider, index) => (
                            <tr key={provider.id}>
                              <td>{index + 1}</td>
                              <td className="fw-bold">{provider.name}</td>
                              <td>
                                <Badge color="info">{provider.count}</Badge>
                              </td>
                              <td className="text-danger fw-bold">
                                {provider.totalFines?.toFixed(2)} ر.س
                              </td>
                              <td className="text-warning">
                                {provider.pendingFines?.toFixed(2)} ر.س
                              </td>
                              <td className="text-success">
                                {provider.deductedFines?.toFixed(2)} ر.س
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  outline
                                  onClick={() => setFilterProvider(provider.id)}
                                >
                                  عرض السجلات
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Pending Provider Fines */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">غرامات مقدمي الخدمة المعلقة للتسوية القادمة</h4>
                    <Button
                      color="light"
                      size="sm"
                      onClick={loadPendingProviderFines}
                      disabled={pendingFinesLoading}
                    >
                      <i className="bx bx-refresh me-1"></i>
                      تحديث
                    </Button>
                  </div>

                  {pendingFinesLoading ? (
                    <div className="text-center py-3">
                      <Spinner color="primary" size="sm" />
                      <p className="mt-2 mb-0">جاري تحميل الغرامات المعلقة...</p>
                    </div>
                  ) : pendingProviderFines.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="table mb-0 table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>مقدم الخدمة</th>
                            <th>رقم الطلب الملغي</th>
                            <th>إجمالي الغرامة</th>
                            <th>المطبق</th>
                            <th>المتبقي</th>
                            <th>الحالة</th>
                            <th>إجراء</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingProviderFines.map((fine, index) => (
                            <tr key={fine.id}>
                              <td>{index + 1}</td>
                              <td>{fine.service_provider_name || fine.service_provider_id || "غير محدد"}</td>
                              <td>{fine.order_id || "--"}</td>
                              <td className="text-danger fw-bold">{Number(fine.original_fine_amount || 0).toFixed(2)} ر.س</td>
                              <td className="text-success">{Number(fine.applied_amount || 0).toFixed(2)} ر.س</td>
                              <td className="text-warning fw-bold">{Number(fine.pending_amount || 0).toFixed(2)} ر.س</td>
                              <td>
                                <Badge color={String(fine.status || "").toLowerCase() === "partial" ? "warning" : "info"}>
                                  {String(fine.status || "").toLowerCase() === "partial" ? "خصم جزئي" : "معلق"}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleApplyPendingFine(fine.id)}
                                  disabled={applyingPendingFineId === fine.id || Number(fine.pending_amount || 0) <= 0}
                                >
                                  {applyingPendingFineId === fine.id ? (
                                    <>
                                      <Spinner size="sm" className="me-1" />
                                      جاري التطبيق...
                                    </>
                                  ) : (
                                    "تطبيق على التسوية القادمة"
                                  )}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert color="light" className="mb-0">لا توجد غرامات معلقة على مقدمي الخدمة حالياً.</Alert>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Main Table */}
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">قائمة الإلغاءات والمخالفات</h4>
                    <div className="d-flex gap-2 align-items-center">
                      {/* Search */}
                      <Input
                        type="text"
                        placeholder="بحث..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: "200px" }}
                      />

                      {/* Provider Filter */}
                      <UncontrolledDropdown>
                        <DropdownToggle color="light" caret>
                          {filterProvider === "all"
                            ? "جميع المقدمين"
                            : providers.find((p) => p.id === filterProvider)
                                ?.name || "مقدم الخدمة"}
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => setFilterProvider("all")}>
                            جميع المقدمين
                          </DropdownItem>
                          <DropdownItem divider />
                          {providers.map((provider) => (
                            <DropdownItem
                              key={provider.id}
                              onClick={() => setFilterProvider(provider.id)}
                            >
                              {provider.name}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>

                      {/* Status Filter */}
                      <div className="btn-group">
                        <Button
                          color={filterStatus === "all" ? "primary" : "light"}
                          size="sm"
                          onClick={() => setFilterStatus("all")}
                        >
                          الكل ({cancellations.length})
                        </Button>
                        <Button
                          color={
                            filterStatus === "pending" ? "primary" : "light"
                          }
                          size="sm"
                          onClick={() => setFilterStatus("pending")}
                        >
                          معلقة (
                          {
                            cancellations.filter((c) =>
                              ["pending", "warning_issued", "confirmed"].includes(c.status)
                            )
                              .length
                          }
                          )
                        </Button>
                        <Button
                          color={
                            filterStatus === "deducted" ? "primary" : "light"
                          }
                          size="sm"
                          onClick={() => setFilterStatus("deducted")}
                        >
                          مخصومة (
                          {
                            cancellations.filter(
                              (c) => c.deduction_status === "deducted"
                            ).length
                          }
                          )
                        </Button>
                      </div>

                      {/* Refresh Button */}
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => {
                          dispatch(fetchAllCancellationsRequest());
                          dispatch(fetchCancellationStatisticsRequest());
                        }}
                      >
                        <i className="bx bx-refresh"></i>
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2">جاري التحميل...</p>
                    </div>
                  ) : error ? (
                    <Alert color="danger">خطأ: {error}</Alert>
                  ) : filteredCancellations.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={filteredCancellations}
                      isGlobalFilter={false}
                      isAddOptions={false}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted">لا توجد سجلات</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Details Modal */}
        <Modal
          isOpen={showDetailsModal}
          toggle={() => setShowDetailsModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowDetailsModal(false)}>
            {isDelayIncident(selectedCancellation) ? "تفاصيل المخالفة" : "تفاصيل الإلغاء"}
          </ModalHeader>
          <ModalBody>
            {selectedCancellation && (
              <div className="cancellation-details">
                {/* Provider Info */}
                <div className="detail-section mb-4">
                  <h6 className="section-title">معلومات مقدم الخدمة</h6>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>الاسم:</strong>{" "}
                        {selectedCancellation.provider_details?.name ||
                          selectedCancellation.service_provider_name}
                      </p>
                      <p>
                        <strong>البريد الإلكتروني:</strong>{" "}
                        {selectedCancellation.provider_details?.email ||
                          selectedCancellation.service_provider_email}
                      </p>
                    </Col>
                    <Col md="6">
                      <p>
                        <strong>الهاتف:</strong>{" "}
                        {selectedCancellation.provider_details?.phone ||
                          selectedCancellation.service_provider_phone}
                      </p>
                      <p>
                        <strong>الشركة:</strong>{" "}
                        {selectedCancellation.provider_details?.company ||
                          selectedCancellation.service_provider_company}
                      </p>
                    </Col>
                  </Row>
                </div>

                {/* Order Info */}
                <div className="detail-section mb-4">
                  <h6 className="section-title">معلومات الطلب</h6>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>رقم الطلب:</strong>{" "}
                        {selectedCancellation.order_id}
                      </p>
                      <p>
                        <strong>الخدمة:</strong>{" "}
                        {selectedCancellation.service_name}
                      </p>
                      <p>
                        <strong>سعر الخدمة:</strong>{" "}
                        {selectedCancellation.booking_amount} ر.س
                      </p>
                    </Col>
                    <Col md="6">
                      <p>
                        <strong>{isDelayIncident(selectedCancellation) ? "تاريخ تسجيل المخالفة:" : "تاريخ الإلغاء:"}</strong>{" "}
                        {formatDate(selectedCancellation.cancellation_date || selectedCancellation.created_at)}
                      </p>
                      <p>
                        <strong>{isDelayIncident(selectedCancellation) ? "نوع المخالفة:" : "نوع الإلغاء:"}</strong>{" "}
                        <Badge
                          color={
                            selectedCancellation.cancellation_type ===
                            "early_cancellation"
                              ? "info"
                              : selectedCancellation.cancellation_type === "provider_delay"
                                ? "warning"
                              : "danger"
                          }
                        >
                          {selectedCancellation.cancellation_type === "early_cancellation"
                            ? "إلغاء مبكر"
                            : selectedCancellation.cancellation_type === "provider_delay"
                              ? "تأخير مقدم الخدمة"
                              : "إلغاء متأخر"}
                        </Badge>
                      </p>
                      {!isDelayIncident(selectedCancellation) && (
                        <p>
                          <strong>الساعات المنقضية:</strong>{" "}
                          {selectedCancellation.hours_elapsed}h
                        </p>
                      )}
                    </Col>
                  </Row>
                </div>

                {/* Fine Details */}
                <div className="detail-section mb-4">
                  <h6 className="section-title">تفاصيل الغرامة</h6>
                  {isDelayIncident(selectedCancellation) ? (
                    <Table bordered size="sm">
                      <tbody>
                        <tr>
                          <td>
                            <strong>ترتيب المخالفة:</strong>
                          </td>
                          <td className="text-end">
                            {selectedCancellation.incident_sequence || 1}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>النتيجة:</strong>
                          </td>
                          <td className="text-end">
                            {selectedCancellation.is_warning_only
                              ? "إنذار بدون غرامة"
                              : "غرامة 30 ر.س على التسوية القادمة"}
                          </td>
                        </tr>
                        <tr className="table-active">
                          <td>
                            <strong>الإجمالي:</strong>
                          </td>
                          <td className="text-end">
                            <strong className={selectedCancellation.total_fine > 0 ? "text-danger" : "text-info"}>
                              {Number(selectedCancellation.total_fine || 0).toFixed(2)} ر.س
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  ) : (
                    <Table bordered size="sm">
                      <tbody>
                        <tr>
                          <td>
                            <strong>رسم النسبة المئوية (2.4%):</strong>
                          </td>
                          <td className="text-end">
                            {selectedCancellation.percentage_fee?.toFixed(2)} ر.س
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>الرسم الثابت:</strong>
                          </td>
                          <td className="text-end">
                            {selectedCancellation.fixed_fee || 2} ر.س
                          </td>
                        </tr>
                        {selectedCancellation.base_fine > 0 && (
                          <tr>
                            <td>
                              <strong>غرامة الإلغاء المتأخر:</strong>
                            </td>
                            <td className="text-end text-warning">
                              {selectedCancellation.base_fine} ر.س
                            </td>
                          </tr>
                        )}
                        <tr className="table-active">
                          <td>
                            <strong>الإجمالي:</strong>
                          </td>
                          <td className="text-end">
                            <strong className="text-danger">
                              {selectedCancellation.total_fine?.toFixed(2)} ر.س
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>

                {/* Deduction Status */}
                <div className="detail-section">
                  <h6 className="section-title">حالة الخصم</h6>
                  <p>
                    <strong>الإلغاء من:</strong>{" "}
                    <Badge
                      color={
                        selectedCancellation.cancelled_by === "app_user"
                          ? "info"
                          : "warning"
                      }
                    >
                      {selectedCancellation.cancelled_by === "app_user"
                        ? "العميل / App User"
                        : "مقدم الخدمة / Service Provider"}
                    </Badge>
                  </p>
                  <p>
                    <strong>الغرامة ستُخصم من:</strong>{" "}
                    <Badge
                      color={
                        selectedCancellation.cancelled_by === "app_user"
                          ? "info"
                          : "warning"
                      }
                    >
                      {selectedCancellation.cancelled_by === "app_user"
                        ? "حساب العميل"
                        : "حساب مقدم الخدمة"}
                    </Badge>
                  </p>
                  <p>
                    <strong>حالة الخصم:</strong>{" "}
                    <Badge
                      color={
                        getDeductionStatusBadge(
                          selectedCancellation.deduction_status
                        ).color
                      }
                    >
                      {
                        getDeductionStatusBadge(
                          selectedCancellation.deduction_status
                        ).text
                      }
                    </Badge>
                  </p>
                  {selectedCancellation.deduction_status === "deducted" && (
                    <>
                      <p>
                        <strong>تاريخ الخصم:</strong>{" "}
                        {formatDate(selectedCancellation.deducted_at)}
                      </p>
                      <p>
                        <strong>مبلغ الخصم:</strong>{" "}
                        <span className="text-danger fw-bold">
                          {selectedCancellation.admin_fine_amount?.toFixed(2)} ر.س
                        </span>
                      </p>
                      <p>
                        <strong>سبب الخصم:</strong>{" "}
                        {selectedCancellation.deduction_reason || "لا يوجد سبب محدد"}
                      </p>
                      
                      {/* Invoice Deductions */}
                      {selectedCancellation.deducted_from_invoices &&
                        selectedCancellation.deducted_from_invoices.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-primary mb-2">
                              <i className="bx bx-receipt me-1"></i>
                              الخصم من الفواتير
                            </h6>
                            <Table bordered size="sm">
                              <tbody>
                                {selectedCancellation.deducted_from_invoices.map(
                                  (inv, idx) => (
                                    <tr key={idx}>
                                      <td className="text-nowrap">
                                        <strong>فاتورة #{idx + 1}:</strong>
                                      </td>
                                      <td className="text-end">
                                        {inv.deduction_amount?.toFixed(2)} ر.س
                                      </td>
                                      <td className="text-muted text-end">
                                        (المتبقي: {inv.remaining_amount?.toFixed(2)} ر.س)
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </Table>
                          </div>
                        )}

                      {/* Settlement Deduction */}
                      {selectedCancellation.remaining_fine_amount > 0 && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <h6 className="text-warning mb-2">
                            <i className="bx bx-time me-1"></i>
                            مبلغ معلق (سيتم خصمه من الفاتورة التالية)
                          </h6>
                          <p className="mb-0">
                            <strong>المبلغ المعلق:</strong>{" "}
                            <span className="text-danger fw-bold">
                              {selectedCancellation.remaining_fine_amount?.toFixed(2)} ر.س
                            </span>
                          </p>
                          <small className="text-muted">
                            سيتم خصم هذا المبلغ تلقائياً من أول فاتورة معلقة لمقدم الخدمة
                          </small>
                        </div>
                      )}

                      {/* Platform Revenue Addition */}
                      {selectedCancellation.added_to_platform_revenue > 0 && (
                        <div className="mt-3 p-3 bg-success-subtle rounded">
                          <h6 className="text-success mb-2">
                            <i className="bx bx-trending-up me-1"></i>
                            إضافة إلى إيرادات المنصة
                          </h6>
                          <p className="mb-0">
                            <strong>المبلغ المضاف:</strong>{" "}
                            <span className="text-success fw-bold">
                              +{selectedCancellation.added_to_platform_revenue?.toFixed(2)} ر.س
                            </span>
                          </p>
                          <small className="text-muted">
                            تمت إضافة مبلغ الغرامة إلى إجمالي عمولات المنصة
                          </small>
                        </div>
                      )}
                    </>
                  )}
                  {selectedCancellation.cancellation_reason && (
                    <p>
                      <strong>{isDelayIncident(selectedCancellation) ? "ملاحظات المخالفة:" : "ملاحظات الإلغاء:"}</strong>{" "}
                      {selectedCancellation.cancellation_reason}
                    </p>
                  )}
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {/* Apply Fine Modal */}
        <Modal
          isOpen={showFineModal}
          toggle={() => setShowFineModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowFineModal(false)}>
            تطبيق الغرامة
          </ModalHeader>
          <ModalBody>
            <Alert color={selectedCancellation?.cancelled_by === "app_user" ? "info" : "warning"}>
              <i className="bx bx-info-circle me-2"></i>
              {selectedCancellation?.cancelled_by === "app_user" 
                ? "سيتم خصم الغرامة من فاتورة مقدم الخدمة المعلقة (مما يقلل المستحقات المدفوعة له والمبلغ المسترجع للعميل)"
                : "سيتم خصم الغرامة من فواتير مقدم الخدمة المعلقة"}
            </Alert>
            
            {selectedCancellation && (
              <Alert color={selectedCancellation?.cancelled_by === "app_user" ? "info" : "warning"} className="mb-3">
                <strong>الإلغاء من:</strong>{" "}
                {selectedCancellation.cancelled_by === "app_user"
                  ? "العميل (App User)"
                  : "مقدم الخدمة (Service Provider)"}
                <br/>
                <strong>الغرامة ستُخصم من:</strong>{" "}
                {selectedCancellation.cancelled_by === "app_user"
                  ? "مستحقات مقدم الخدمة (يقلل المبلغ المسترجع للعميل)"
                  : "مستحقات مقدم الخدمة"}
              </Alert>
            )}
            <Alert color="info">
              <div className="mb-2">
                <i className="bx bx-note me-2"></i>
                <strong>سبب الخصم:</strong> إلغاء متأخر - الطلب #{selectedCancellation?.order_id}
              </div>
              <small className="text-muted">
                <i className="bx bx-coin me-1"></i>
                المبلغ المخصوم سيتم إضافته إلى إيرادات المنصة (عمولة إضافية)
              </small>
            </Alert>
            <Alert color="success">
              <i className="bx bx-check-circle me-2"></i>
              <small>
                {selectedCancellation?.cancelled_by === "app_user"
                  ? "سيتم البحث عن فاتورة هذا العميل من مقدم الخدمة المحدد بحالة معلقة فقط. إذا لم توجد، لن يتم تطبيق الغرامة."
                  : "سيتم البحث عن الفواتير ذات الحالة معلقة فقط. إذا لم توجد فواتير معلقة، لن يتم تطبيق الغرامة."}
              </small>
            </Alert>
            <FormGroup>
              <Label>مبلغ الغرامة (ر.س)</Label>
              <Input
                type="number"
                value={fineAmount}
                onChange={(e) => setFineAmount(e.target.value)}
                placeholder="أدخل مبلغ الغرامة"
                step="0.01"
                min="0"
              />
            </FormGroup>
            {selectedCancellation && (
              <div className="mt-3">
                <p className="mb-2">
                  <strong>مقدم الخدمة:</strong>{" "}
                  {selectedCancellation.service_provider_name}
                </p>
                <p className="mb-2">
                  <strong>رقم الطلب:</strong> {selectedCancellation.order_id}
                </p>
                <p className="mb-0">
                  <strong>الغرامة المقترحة:</strong>{" "}
                  {selectedCancellation.total_fine?.toFixed(2)} ر.س
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowFineModal(false)}
              disabled={fineLoading}
            >
              إلغاء
            </Button>
            <Button
              color="danger"
              onClick={handleApplyFine}
              disabled={fineLoading}
            >
              {fineLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  جاري التطبيق...
                </>
              ) : (
                "تطبيق الغرامة"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default CancellationTracking;
